/**
 * Persistent ETag Storage
 * Uses localStorage in browser or in-memory fallback in Node.js
 */

interface ETagEntry {
  etag: string;
  timestamp: number;
}

class PersistentETagStore {
  private memoryStore: Map<string, ETagEntry> = new Map();
  private readonly STORAGE_KEY_PREFIX = 'rr_etag_';
  private readonly EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

  constructor() {
    // Clean up expired entries on init (client-side only)
    if (typeof window !== 'undefined') {
      this.cleanup();
    }
  }

  get(key: string): string | undefined {
    // Try memory first
    const memoryEntry = this.memoryStore.get(key);
    if (memoryEntry) {
      if (Date.now() - memoryEntry.timestamp > this.EXPIRY_MS) {
        this.memoryStore.delete(key);
        return undefined;
      }
      return memoryEntry.etag;
    }

    // Try localStorage (client-side only)
    if (typeof window !== 'undefined') {
      try {
        const item = localStorage.getItem(this.STORAGE_KEY_PREFIX + key);
        if (item) {
          const entry = JSON.parse(item) as ETagEntry;
          if (Date.now() - entry.timestamp > this.EXPIRY_MS) {
            localStorage.removeItem(this.STORAGE_KEY_PREFIX + key);
            return undefined;
          }
          // Sync to memory
          this.memoryStore.set(key, entry);
          return entry.etag;
        }
      } catch {
        // Ignore storage errors
      }
    }

    return undefined;
  }

  set(key: string, etag: string): void {
    const entry: ETagEntry = {
      etag,
      timestamp: Date.now(),
    };

    // Update memory
    this.memoryStore.set(key, entry);

    // Update localStorage (client-side only)
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(this.STORAGE_KEY_PREFIX + key, JSON.stringify(entry));
      } catch {
        // Ignore storage errors (e.g. quota exceeded)
      }
    }
  }

  delete(key: string): void {
    this.memoryStore.delete(key);
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(this.STORAGE_KEY_PREFIX + key);
      } catch {
        // Ignore storage errors
      }
    }
  }

  private cleanup(): void {
    try {
      const now = Date.now();
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          try {
            const item = localStorage.getItem(key);
            if (item) {
              const entry = JSON.parse(item) as ETagEntry;
              if (now - entry.timestamp > this.EXPIRY_MS) {
                localStorage.removeItem(key);
              }
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
    } catch {
      // Ignore storage errors
    }
  }
}

export const etagStore = new PersistentETagStore();

